import type Handsontable from 'handsontable';
import { gridRecordCellRow } from './as400GridCellRules';
import {
  isGridRecord,
  type As400GridRecord,
  type GridCellChange,
} from './as400ExportEditState';

/**
 * Select and scroll to the cell of the record keyed `rowKey` bound to
 * `field`, without taking keyboard focus. False when either is not in the
 * grid, or the cell is hidden: Handsontable's `selectCell` only refuses
 * coordinates outside the grid, and lands on a hidden row or column as
 * readily as a shown one (the index mappers say which is which).
 */
export function selectRecordCell(
  instance: Handsontable,
  rowKey: string,
  field: string,
): boolean {
  const col = instance.propToCol(field);
  if (typeof col !== 'number' || col < 0) return false;
  if (instance.columnIndexMapper.isHidden(instance.toPhysicalColumn(col))) {
    return false;
  }
  for (let physical = 0; physical < instance.countSourceRows(); physical += 1) {
    const record = instance.getSourceDataAtRow(physical);
    if (!isGridRecord(record) || record.__key !== rowKey) continue;
    if (instance.rowIndexMapper.isHidden(physical)) return false;
    const row = instance.toVisualRow(physical);
    return instance.selectCell(row, col, row, col, true, false);
  }
  return false;
}

/** One grid cell by visual coordinates. */
export interface GridCell {
  row: number;
  col: number;
}

/** A selected range, as Handsontable's `getSelectedRange()` reports one. */
export interface GridRange {
  from: GridCell;
  to: GridCell;
}

/**
 * Every cell the ranges cover, once each, in range order — a non-contiguous
 * (Ctrl/Cmd-click) selection is several ranges. Header coordinates (negative,
 * from a whole-row or whole-column selection) are clamped to the first body
 * row or column.
 */
export function cellsInRanges(ranges: readonly GridRange[]): GridCell[] {
  const seen = new Set<string>();
  const cells: GridCell[] = [];
  for (const { from, to } of ranges) {
    const rowTo = Math.max(from.row, to.row);
    const colTo = Math.max(from.col, to.col);
    for (
      let row = Math.max(Math.min(from.row, to.row), 0);
      row <= rowTo;
      row += 1
    ) {
      for (
        let col = Math.max(Math.min(from.col, to.col), 0);
        col <= colTo;
        col += 1
      ) {
        const key = `${row}:${col}`;
        if (seen.has(key)) continue;
        seen.add(key);
        cells.push({ row, col });
      }
    }
  }
  return cells;
}

/** What a selected cell is bound to, when the grid lets it be written. */
export interface WritableCell {
  prop: string;
  record: As400GridRecord;
}

/** Finds a cell's {@link WritableCell}, or null when it can't be written. */
export type WritableCellAt = (cell: GridCell) => WritableCell | null;

/** A column's AS/400 blank-out marker, by the field it is bound to. */
export type BlankMarkerFor = (prop: string) => string | undefined;

const NO_BLANK_MARKERS: BlankMarkerFor = () => undefined;

/**
 * Whether a typed value is its column's blank-out marker — which, landing in
 * other columns, means "blank them too", not "type these slashes there".
 */
function typedBlankMarker(
  prop: unknown,
  value: unknown,
  blankMarkerFor: BlankMarkerFor,
): boolean {
  return typeof prop === 'string' && blankMarkerFor(prop) === value;
}

/**
 * The value typed into one cell, landing in every other selected cell too.
 * `change` is the editor's commit (`[row, prop, old, new]`); each other cell
 * the grid lets be written gets the same new value over its own old one.
 * Cells it can't write ({@link writableCellAt}: read-only, hidden, synthetic)
 * are skipped, as is the change's own cell.
 *
 * Typing a column's blank-out marker is the exception: markers differ in
 * length per column (`/` vs `/////`), so each other cell gets its own
 * column's marker, and a cell whose column has none is left alone.
 */
export function fanOutEdit(
  change: GridCellChange,
  cells: readonly GridCell[],
  writableAt: WritableCellAt,
  blankMarkerFor: BlankMarkerFor = NO_BLANK_MARKERS,
): GridCellChange[] {
  const [row, prop, , newValue] = change;
  const blanking = typedBlankMarker(prop, newValue, blankMarkerFor);
  const changes: GridCellChange[] = [];
  for (const cell of cells) {
    const target = writableAt(cell);
    if (!target || (cell.row === row && target.prop === prop)) continue;
    const value = blanking ? blankMarkerFor(target.prop) : newValue;
    if (blanking && value === undefined) continue;
    changes.push([cell.row, target.prop, target.record[target.prop], value]);
  }
  return changes;
}

/** One cell put back to an earlier value: `[row, prop, value]`. */
export type CellReset = [row: number, prop: string, value: string | null];

/** The cell reset functions' shared shape: which cells change, and to what. */
export type CellResetsFor = (
  cells: readonly GridCell[],
  writableAt: WritableCellAt,
) => CellReset[];

/**
 * A write for every writable cell among `cells` whose `target` differs from
 * what it shows. The writes go through the same afterChange → applyCellEdit
 * path as typing them.
 */
function resetsTo(
  cells: readonly GridCell[],
  writableAt: WritableCellAt,
  target: (record: As400GridRecord, prop: string) => string | null,
): CellReset[] {
  const resets: CellReset[] = [];
  for (const cell of cells) {
    const written = writableAt(cell);
    if (!written) continue;
    const { prop, record } = written;
    const value = target(record, prop);
    if (gridRecordCellRow(record).value(prop) === value) continue;
    resets.push([cell.row, prop, value]);
  }
  return resets;
}

/**
 * Put every cell among `cells` with an unsaved edit back to its last saved
 * value — its saved (staged) edit, else live. Writing what is persisted drops
 * the pending edit ({@link applyCellEdit} removes a field typed back to it);
 * saved edits stay as they are.
 */
export const cellResetsToSaved: CellResetsFor = (cells, writableAt) =>
  resetsTo(
    cells,
    writableAt,
    (record, prop) => record.__persisted[prop] ?? null,
  );

/**
 * Put every cell among `cells` back to what the AS/400 has — its revert value
 * ({@link revertCellValue}), discarding unsaved and saved edits alike. An
 * unsaved-only edit drops out; a saved one becomes a pending edit to the
 * revert value, which Save sends and the server reverts.
 */
export const cellResetsToAs400: CellResetsFor = (cells, writableAt) =>
  resetsTo(cells, writableAt, (record, prop) => record.__revert[prop] ?? null);

/**
 * {@link WritableCellAt} over a live grid: a shown cell in an editable column
 * bound to a real field (never a synthetic `__` one) of one of our records.
 */
export function writableCellAt(instance: Handsontable): WritableCellAt {
  return ({ row, col }) => {
    const physicalRow = instance.toPhysicalRow(row);
    if (instance.rowIndexMapper.isHidden(physicalRow)) return null;
    if (instance.columnIndexMapper.isHidden(instance.toPhysicalColumn(col))) {
      return null;
    }
    const prop = instance.colToProp(col);
    if (typeof prop !== 'string' || prop.startsWith('__')) return null;
    if (instance.getCellMeta(row, col).readOnly) return null;
    const record = instance.getSourceDataAtRow(physicalRow);
    return isGridRecord(record) ? { prop, record } : null;
  };
}

/** Every selected cell of a live grid, across all its ranges. */
export function selectedCells(instance: Handsontable): GridCell[] {
  return cellsInRanges(instance.getSelectedRange() ?? []);
}

function selectedCellsIfHolding(
  instance: Handsontable,
  row: number,
  col: number,
): GridCell[] {
  const cells = selectedCells(instance);
  return cells.some((cell) => cell.row === row && cell.col === col)
    ? cells
    : [];
}

/**
 * Widen the open editor's commit to the whole selection, in place: typing
 * with several cells selected writes the value to all of them. Only the
 * editor's own single-cell commit is widened — a paste, an autofill, a
 * Delete over a range or Ctrl+Enter already name every cell they change.
 */
export function expandEditToSelection(
  instance: Handsontable,
  changes: Array<GridCellChange | null>,
  blankMarkerFor: BlankMarkerFor = NO_BLANK_MARKERS,
): void {
  const editor = instance.getActiveEditor();
  const [change] = changes;
  if (!editor?.isOpened() || changes.length !== 1 || !change) return;
  const [row, prop, oldValue, newValue] = change;
  // Enter (or F2, or a double-click) opens the editor in full edit mode, and
  // pressing Enter again without typing commits the value unchanged — that
  // must not copy the cell across the selection. Typing over a cell opens it
  // in fast edit mode, where retyping the same value (say, a blank-out
  // marker the active cell already holds) is meant for the whole selection.
  if (oldValue === newValue && editor.isInFullEditMode()) return;
  if (row !== editor.row || prop !== instance.colToProp(editor.col)) return;
  changes.push(
    ...fanOutEdit(
      change,
      selectedCellsIfHolding(instance, editor.row, editor.col),
      writableCellAt(instance),
      blankMarkerFor,
    ),
  );
}
